import { getAssetUrl } from '@shared/constants';

/**
 * Cursores custom del juego (archivos .cur en `client/assets/cursors/`). El nombre del archivo
 * = la key. Cada uno lleva su hotspot embebido en el formato .cur (los navegadores lo respetan).
 *
 * - `main`    — cursor por defecto en todo el juego.
 * - `click`   — sobre algo accionable (botones del HUD, item del piso recogible).
 * - `drag`    — mientras se arrastra una ventana/panel o un item.
 * - `talk`    — sobre un NPC con el que se dialoga (NPC interactivo genérico).
 * - `shop`    — sobre un NPC vendedor de items (merchant).
 * - `enchant` — sobre un NPC vendedor de hechizos (spell-vendor) o sacerdote.
 * - `quest`   — sobre un NPC que ofrece misiones (`questIds`).
 * - `text`    — caret de edición sobre inputs/textarea de texto.
 */
export type GameCursor = 'main' | 'click' | 'drag' | 'talk' | 'shop' | 'enchant' | 'quest' | 'text';

// Fallback CSS por si el .cur no carga (Chrome ignora cursores > ~32px). SIEMPRE al final de la
// cadena: si la imagen falla, el navegador usa el keyword en vez de descartar toda la regla.
const FALLBACK: Record<GameCursor, string> = {
  main: 'default',
  click: 'pointer',
  drag: 'grabbing',
  talk: 'pointer',
  shop: 'pointer',
  enchant: 'pointer',
  quest: 'pointer',
  text: 'text',
};

/** Devuelve el valor CSS `cursor` para un cursor del juego: `url(...assets/cursors/x.cur), <kw>`. */
export function cursorCss(name: GameCursor): string {
  return `url(${getAssetUrl(`cursors/${name}.cur`)}), ${FALLBACK[name]}`;
}

/**
 * Valor CSS para headers DOM arrastrables (paneles/ventanas): el cursor `drag` con fallback
 * `move` (en vez de `grabbing`), que es el keyword esperado para arrastrar una ventana.
 */
export function domDragCursorCss(): string {
  return `url(${getAssetUrl('cursors/drag.cur')}), move`;
}

const GLOBAL_STYLE_ID = 'aureum-cursors';

/**
 * Aplica los cursores `.cur` a TODO el documento (no sólo al canvas del juego): login, paneles
 * DOM, links, botones. Inyecta una hoja de estilos global (idempotente por id). Como `cursor`
 * es una propiedad CSS heredable, `html, body` cubre casi todo; los accionables van con
 * `!important` para pisar los `style.cursor='pointer'` inline de la UI. Los campos de texto
 * mantienen el caret. Llamar una vez al arranque (antes de renderizar el login).
 */
export function installGlobalCursors(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(GLOBAL_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = GLOBAL_STYLE_ID;
  const click = cursorCss('click');
  const drag = cursorCss('drag');
  style.textContent = `
    html, body { cursor: ${cursorCss('main')}; }

    /* Accionables por tipo de elemento → click.cur (cubre botones/links sin cursor inline). */
    a, button, [role="button"], label, select,
    input[type="checkbox"], input[type="radio"], input[type="color"],
    input[type="submit"], input[type="button"], input[type="range"] {
      cursor: ${click} !important;
    }

    /* Captura TODO lo que setee el cursor inline a pointer/move — sirve tanto para divs/spans
       de la UI DOM como para el CANVAS de Phaser (useHandCursor:true pone style.cursor='pointer'
       en el canvas → así reemplazamos el pointer del navegador en TODOS los paneles del juego
       sin tocar los 129 setInteractive). El !important pisa el inline. */
    [style*="cursor: pointer"], [style*="cursor:pointer"] { cursor: ${click} !important; }
    [style*="cursor: move"], [style*="cursor:move"]       { cursor: ${drag} !important; }

    /* Campos de texto: caret de edición custom (text.cur). */
    input[type="text"], input[type="password"], input[type="email"],
    input[type="number"], input[type="search"], textarea,
    [style*="cursor: text"], [style*="cursor:text"] {
      cursor: ${cursorCss('text')} !important;
    }
  `;
  document.head.appendChild(style);
}
